import {
  PrismicAllProjectsQuery,
  PrismicProjectFilterQuery,
} from '@prismic/gqlTypes/prismicOperations';

import { useState } from 'react';
import { NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';

import { useQueryState } from '@hooks/useQueryState';

import {
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
  sortProjectsByCreationDate,
} from './utils';

import Page from './Page';

export interface ViewProps {
  allProjects: PrismicAllProjectsQuery;
  projectFilters: PrismicProjectFilterQuery;
  seo: NextSeoProps | null;
}

const View: NextPage<ViewProps> = ({ allProjects, projectFilters, seo }) => {
  const pageCount = 12;

  const [attributeFilters, setAttributeFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: {},
  });

  const [page, setPage] = useState(1);

  const filters = {
    attributes: attributeFilters,
  };

  const clearFilters = () => {
    setAttributeFilters({});
    setPage(1);
  };

  const onFiltersChange = (name, value) => {
    if (Array.isArray(value)) {
      // handle range of values
      setAttributeFilters({ ...attributeFilters, [`${name}`]: value });
      setPage(1);
      return;
    }

    if (value === null) {
      // handle clear filter
      const att = { ...attributeFilters };
      delete att[`${name}`];
      setAttributeFilters({
        ...att,
      });
      setPage(1);
      return;
    }
    // TODO: fixed eslint error. Check it.
    // if (attributeFilters && attributeFilters.hasOwnProperty(name)) {
    if (attributeFilters && name in attributeFilters) {
      if (attributeFilters[name].includes(value)) {
        if (filters.attributes[`${name}`].length === 1) {
          const att = { ...attributeFilters };
          delete att[`${name}`];
          setAttributeFilters({
            ...att,
          });
        } else {
          setAttributeFilters({
            ...attributeFilters,
            [`${name}`]: attributeFilters[`${name}`].filter(
              (item) => item !== value
            ),
          });
        }
      } else {
        setAttributeFilters({
          ...attributeFilters,
          [`${name}`]: [...attributeFilters[`${name}`], value],
        });
      }
    } else {
      setAttributeFilters({ ...attributeFilters, [`${name}`]: [value] });
    }
    setPage(1);
  };

  const onLoadMore = () => {
    setPage(page + 1);
  };

  const attributes = [
    {
      id: 'category',
      name: 'category',
      slug: 'category',
      values: !projectFilters
        ? []
        : projectFilters.allProjectcategorys.edges.map((z) => ({
            id: z.node._meta.id,
            name: z.node.name,
            slug: z.node._meta.uid,
            url: z.node.image.url,
          })),
    },
    {
      id: 'tag',
      name: 'tag',
      slug: 'tag',
      values: !projectFilters
        ? []
        : projectFilters.allProjecttags.edges.map((z) => ({
            id: z.node._meta.id,
            name: z.node.name,
            slug: z.node._meta.uid,
          })),
    },
  ];

  const projectsAll = !allProjects
    ? []
    : sortProjectsByCreationDate(allProjects.allProjects.edges)
        .filter((z) => {
          if (filters.attributes === undefined) {
            return true;
          }

          const checkFilters = (key, val): boolean => {
            if (filters.attributes[key]) {
              return filters.attributes[key].includes(val);
            }
            return true;
          };

          const hasCategory = (z.node.category_list || []).some((x) =>
            checkFilters('category', (x?.category as any)?._meta?.uid)
          );

          const hasTag = z.node.tag_list.some((x) =>
            checkFilters(
              'tag',
              x.tag_item && '_meta' in x.tag_item && x.tag_item._meta.uid
            )
          );

          return hasCategory && hasTag;
          // return hasCategory && hasColor && hasWidth && hasTag;
        })
        .map((z) => ({
          category: (z.node.category_list || [])
            .map((x) => x?.category?.name || '')
            .filter((x) => Boolean(x))
            .join(`, `),
          id: z.node._meta.id,
          image: z.node.image,
          name: z.node._meta.uid,
          title: z.node.title,
        }));
  const end = page * pageCount;
  const projects = projectsAll.slice(0, end);
  const hasNextPage = projectsAll.length > end;

  return (
    <>
      <NextSeo {...seo} />
      <Page
        clearFilters={clearFilters}
        attributes={attributes}
        filters={filters}
        projects={projects}
        onAttributeFiltersChange={onFiltersChange}
        hasNextPage={hasNextPage}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

View.displayName = 'AllTypesView';

export default View;
